import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { unmaskNumber } from '@givebutter/util';
import { faPenToSquare } from '@fortawesome/pro-solid-svg-icons';
import { updateAuctionBidder } from 'api/campaigns';
import * as Form from 'components/form';
import { useCurrentCampaign } from 'customHooks/useCurrentCampaign';
import { useForceRender } from 'customHooks/useForceRender';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Bidder } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface UpdateBidderNumberDialogProps extends AlertDialog.AlertDialogRootProps {
    bidder: Bidder;
}

interface UpdateBidderNumberFormValues {
    number: number;
}

const UpdateBidderNumberDialog = (props: UpdateBidderNumberDialogProps) => {
    const queryClient = useQueryClient();
    const { children, bidder, ...rest } = props;
    const { data: campaign } = useCurrentCampaign();
    const campaignId = Number(campaign.id);
    const auctionId = Number(campaign.auction_id);
    const { success, error } = useToast();
    const { key, forceRender } = useForceRender();

    const methods = Form.useGivebutterForm<UpdateBidderNumberFormValues>({
        defaultValues: {
            number: bidder.number,
        },
    });

    const { handleSubmit, control, path, setError, resetFormButton } = methods;

    const { mutate: updateBidderNumber } = useMutation({
        mutationFn: ({ number }: UpdateBidderNumberFormValues) =>
            updateAuctionBidder(campaignId, auctionId, bidder.id, {
                number: unmaskNumber(number),
            }),

        onSuccess: () => {
            success('Bidder number updated successfully.');
            queryClient.invalidateQueries({
                queryKey: campaignQueryKeys.auctionBidders(campaignId, auctionId),
            });
            forceRender();
        },
        onError: (err: GivebutterAxiosError) => {
            const data = err.response?.data;
            const errors = data?.errors;

            if (typeof errors === 'object') {
                for (let field in errors) {
                    setError(field as keyof UpdateBidderNumberFormValues, {
                        message: errors[field][0],
                    });
                }
            } else {
                error(data?.message ?? 'Failed to update bidder number');
            }

            resetFormButton();
        },
    });

    return (
        <AlertDialog.Root key={key} type="info" {...rest}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content className="max-w-sm p-6">
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faPenToSquare} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <Form.Provider {...methods}>
                        <form onSubmit={handleSubmit(values => updateBidderNumber(values))}>
                            <AlertDialog.Body>
                                <div className="flex flex-col gap-3">
                                    <AlertDialog.Title>Edit bidder #</AlertDialog.Title>
                                    <AlertDialog.Description>
                                        Edit the unique identifier number for this bidder.
                                    </AlertDialog.Description>
                                    <Form.Container>
                                        <Form.Label className="sr-only" name={path('number')}>
                                            Bidder Number
                                        </Form.Label>
                                        <Form.TextInputRoot
                                            name={path('number')}
                                            control={control}
                                            rules={{
                                                required: 'Starting bidder number is required.',
                                                validate: {
                                                    greaterThanZero: value =>
                                                        unmaskNumber(value) >= 1 ||
                                                        'The bidder number start must be at least 1.',
                                                },
                                            }}
                                            inputRootProps={{
                                                mask: 'whole-number',
                                            }}
                                        >
                                            <Form.TextInputForm />
                                        </Form.TextInputRoot>
                                        <Form.Error name={path('number')} />
                                    </Form.Container>
                                </div>
                            </AlertDialog.Body>
                            <AlertDialog.Footer>
                                <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                <Form.SubmitButton className="w-1/2">Save</Form.SubmitButton>
                            </AlertDialog.Footer>
                        </form>
                    </Form.Provider>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const UpdateBidderNumberDialogTrigger = AlertDialog.Trigger;

const Root = UpdateBidderNumberDialog;
const Trigger = UpdateBidderNumberDialogTrigger;

export {
    Root,
    Trigger,
    //
    UpdateBidderNumberDialog,
    UpdateBidderNumberDialogTrigger,
};
